import { Box, Button, Form, FormInput } from '@auviknetworks/iceberg-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { Card, Typography } from '@mui/material';
import { Row, RowModel } from '@tanstack/react-table';
import { useManagedTableV2Context } from 'application/Shared/Components/Table';
import { User } from 'application/Shared/types/user';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import * as yup from 'yup';

import { FixedPositionPageFooter } from './FixedPositionPageFooter';
import { RecipeWithTenantApp } from './types';

const PageFooter = () => {
    const { t } = useTranslation();

    const { formState } = useFormContext();
    const { isValid } = formState;

    const { table } = useManagedTableV2Context();
    const noAppsSelected = table?.getSelectedRowModel().rows.length === 0;

    const disabled = !isValid || noAppsSelected;

    return (
        <FixedPositionPageFooter>
            <Button
                data-trackid="onboardUser-userAppsSelected-next"
                type="submit"
                disabled={disabled}
            >
                {t('Common.next')}
            </Button>
        </FixedPositionPageFooter>
    );
};

const FormFields = () => {
    const { t } = useTranslation();

    return (
        <Box pt="24px" width="100%">
            <FormInput
                name="firstName"
                required
                autoFocus
                label={t('Common.first_name')}
                sx={{ mb: '24px', width: '75%' }}
            />

            <FormInput
                name="lastName"
                label={t('Common.last_name')}
                required
                sx={{ mb: '24px', width: '75%' }}
            />

            <FormInput
                name="email"
                label={t('Common.email')}
                required
                sx={{ mb: '24px', width: '75%' }}
            />
        </Box>
    );
};

export const OnboardUserDetails = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { table } = useManagedTableV2Context();

    const defaultValues = {
        email: searchParams.get('email') || '',
        firstName: searchParams.get('firstName') || '',
        lastName: searchParams.get('lastName') || ''
    } as Partial<User>;

    const yupValidator = yupResolver(
        yup.object({
            email: yup
                .string()
                .email(t('Form.validEmail'))
                .trim()
                .required(t('Form.fieldRequired')),
            firstName: yup.string().trim().required(t('Form.fieldRequired')),
            lastName: yup.string().trim().required(t('Form.fieldRequired'))
        })
    );

    const onSubmit = (values: Partial<User>) => {
        const { rows } =
            table.getSelectedRowModel() as RowModel<RecipeWithTenantApp>;

        const applicationIds = rows.map(
            (row: Row<RecipeWithTenantApp>) => row.original.applicationId
        );

        setSearchParams((currentParams) => {
            currentParams.set('applicationId', applicationIds.join(','));
            currentParams.set('firstName', values.firstName!);
            currentParams.set('lastName', values.lastName!);
            currentParams.set('email', values.email!);
            currentParams.set(
                'name',
                `${values?.firstName} ${values?.lastName}`
            );

            return currentParams;
        });
    };
    return (
        <Card variant="outlined">
            <Box sx={{ margin: '16px' }}>
                <Typography variant="h6" color="textSecondary" fontWeight={600}>
                    {t('Common.user_details')}
                </Typography>
                <hr />
                <Box>
                    <Form<Partial<User>>
                        onSubmit={onSubmit}
                        mode="all"
                        validate={yupValidator}
                        defaultValues={defaultValues}
                    >
                        <FormFields />
                        <PageFooter />
                    </Form>
                </Box>
            </Box>
        </Card>
    );
};
